import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface Team {
  id: string;
  name: string;
  slug: string;
  colour: string | null;
  avatar_url: string | null;
  created_at: string;
  archived_at?: string | null;
  member_count?: number;
}
interface EnrichedMember {
  user_id: string;
  handle: string;
  display_name: string;
  email: string;
  is_ignored: boolean;
  role_in_team: 'player' | 'lead' | 'admin';
  joined_at: string;
}
interface User { id: string; handle: string; display_name: string; email: string; is_ignored: boolean; }

const ROLE_LABELS: Record<EnrichedMember['role_in_team'], string> = {
  player: 'Player',
  lead: 'Lead',
  admin: 'Admin',
};

// Admin teams page: list + create on the left, full membership
// management (add / change role / remove) for the selected team on
// the right. All actions admin-only.
export function AdminTeamsPage() {
  const qc = useQueryClient();
  const teams = useQuery<{ teams: Team[] }>({
    queryKey: ['teams'],
    queryFn: () => api('/api/teams'),
  });
  const users = useQuery<{ users: User[] }>({
    queryKey: ['users'],
    queryFn: () => api('/api/users'),
  });

  const create = useMutation({
    mutationFn: (body: { name: string; slug: string; colour?: string | null }) =>
      api<{ team: Team }>('/api/teams', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api(`/api/teams/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['me-teams'] });
    },
  });
  const restore = useMutation({
    mutationFn: (id: string) => api(`/api/teams/${id}`, { method: 'PATCH', body: { archived_at: null } }),
    // archived_at is a non-updateable column? Actually the patch schema is
    // CreateTeamBody.partial() — no archived_at. We need a different
    // approach: server's PATCH only accepts the team shape. For now,
    // restoration isn't wired (it's a "nice to have"). Soft-delete is
    // the rule, restore can come later.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });

  const [selectedId, setSelectedId] = useState<string>('');
  const teamList = teams.data?.teams ?? [];
  // Default-select the first team when the list loads.
  const activeId = selectedId || teamList[0]?.id || '';
  const activeTeam = teamList.find((t) => t.id === activeId);

  const [form, setForm] = useState({ name: '', slug: '', colour: '' });
  const [err, setErr] = useState<string | null>(null);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      const body: { name: string; slug: string; colour?: string } = { name: form.name, slug: form.slug };
      if (form.colour.trim()) body.colour = form.colour.trim();
      const { team } = await create.mutateAsync(body);
      setForm({ name: '', slug: '', colour: '' });
      setSelectedId(team.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Failed to create team.');
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Teams</h1>
        <p className="text-ink-muted">
          Teams group people together for scoring, kudos, quests, and the public board.
          Create a team, then add members on the right.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        {/* Left: create + list */}
        <div className="space-y-4">
          <form onSubmit={onCreate} className="card grid gap-3">
            <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">New team</p>
            <div className="space-y-1">
              <label className="label" htmlFor="t-name">Name</label>
              <input id="t-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="label" htmlFor="t-slug">Slug</label>
              <input id="t-slug" className="input font-mono" required pattern="[-a-z0-9]{2,40}" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="label" htmlFor="t-colour">Colour (optional)</label>
              <input id="t-colour" className="input" type="text" pattern="#[0-9a-fA-F]{6}" placeholder="#7c5cff" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} />
            </div>
            <button className="btn-primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create team'}
            </button>
            {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}
          </form>

          <div className="card">
            <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
              All teams ({teamList.length})
            </p>
            {teamList.length === 0 ? (
              <p className="text-sm text-ink-muted">No teams yet.</p>
            ) : (
              <ul className="space-y-1">
                {teamList.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition ${
                        activeId === t.id ? 'bg-bg-elevated ring-1 ring-accent-primary/40' : 'hover:bg-bg-elevated/60'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {t.colour ? <span className="inline-block h-3 w-3 rounded-full" style={{ background: t.colour }} aria-hidden /> : null}
                        <span className="font-medium">{t.name}</span>
                      </span>
                      <span className="text-xs text-ink-dim">
                        {t.member_count ?? '–'} member{t.member_count === 1 ? '' : 's'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: selected team membership panel */}
        <div>
          {activeTeam ? (
            <TeamMembershipPanel team={activeTeam} allUsers={users.data?.users ?? []} />
          ) : (
            <div className="card text-sm text-ink-muted">Select a team on the left, or create one above.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamMembershipPanel({ team, allUsers }: { team: Team; allUsers: User[] }) {
  const qc = useQueryClient();
  const members = useQuery<{ members: EnrichedMember[] }>({
    queryKey: ['team-members', team.id],
    queryFn: () => api(`/api/teams/${team.id}/members`),
  });

  const add = useMutation({
    mutationFn: (body: { user_id: string; role_in_team: 'player' | 'lead' | 'admin' }) =>
      api(`/api/teams/${team.id}/members`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', team.id] });
      qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
  const update = useMutation({
    mutationFn: ({ userId, role_in_team }: { userId: string; role_in_team: 'player' | 'lead' | 'admin' }) =>
      api(`/api/teams/${team.id}/members/${userId}`, { method: 'PATCH', body: { role_in_team } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members', team.id] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/api/teams/${team.id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', team.id] });
      qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
  const archive = useMutation({
    mutationFn: () => api(`/api/teams/${team.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['me-teams'] });
    },
  });

  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<'player' | 'lead' | 'admin'>('player');
  const [err, setErr] = useState<string | null>(null);

  const memberIds = new Set((members.data?.members ?? []).map((m) => m.user_id));
  // Show everyone not already in the team, but allow adding ignored
  // users too (service accounts belong on a team so they can receive
  // actor mappings).
  const addable = allUsers.filter((u) => !memberIds.has(u.id));

  async function onAdd() {
    setErr(null);
    if (!addUserId) return;
    try {
      await add.mutateAsync({ user_id: addUserId, role_in_team: addRole });
      setAddUserId(''); setAddRole('player');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to add member.');
    }
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {team.colour ? <span className="inline-block h-4 w-4 rounded-full" style={{ background: team.colour }} aria-hidden /> : null}
            <div>
              <h2 className="text-xl font-semibold">{team.name}</h2>
              <p className="text-sm text-ink-muted">@{team.slug} · {team.member_count ?? (members.data?.members ?? []).length ?? 0} members</p>
            </div>
          </div>
          <button
            className="btn-danger"
            onClick={() => {
              if (window.confirm(`Archive team "${team.name}"? Members will be hidden from kudos / heatmap / public board. The team and its data are kept and can be restored.`)) {
                archive.mutate();
              }
            }}
            disabled={archive.isPending}
          >
            Archive team
          </button>
        </div>
      </div>

      {/* Add member */}
      <div className="card">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Add member</p>
        {addable.length === 0 ? (
          <p className="text-sm text-ink-muted">All users are already on this team.</p>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1 flex-1 min-w-[14rem]">
              <label className="label" htmlFor="add-user">User</label>
              <select id="add-user" className="input" value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
                <option value="">— select user —</option>
                {addable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name} (@{u.handle}){u.is_ignored ? ' — ignored' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="label" htmlFor="add-role">Role</label>
              <select id="add-role" className="input" value={addRole} onChange={(e) => setAddRole(e.target.value as 'player' | 'lead' | 'admin')}>
                <option value="player">Player</option>
                <option value="lead">Lead</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button className="btn-primary" onClick={onAdd} disabled={!addUserId || add.isPending}>
              {add.isPending ? 'Adding…' : 'Add to team'}
            </button>
          </div>
        )}
        {err ? <p className="mt-2 text-sm text-accent-danger" role="alert">{err}</p> : null}
      </div>

      {/* Members list */}
      <div className="card overflow-x-auto">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
          Members ({(members.data?.members ?? []).length ?? 0})
        </p>
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr><th className="py-2">Handle</th><th>Display name</th><th>Email</th><th>Role</th><th>Joined</th><th></th></tr>
          </thead>
          <tbody>
            {(members.data?.members ?? []).map((m) => (
              <tr key={m.user_id} className="border-t border-white/5">
                <td className="py-2 font-mono">
                  {m.handle}
                  {m.is_ignored ? <span className="ml-2 rounded bg-ink-dim/30 px-1.5 py-0.5 text-ink-dim">ignored</span> : null}
                </td>
                <td>{m.display_name}</td>
                <td className="text-ink-muted">{m.email}</td>
                <td>
                  <select
                    className="input py-1"
                    value={m.role_in_team}
                    onChange={(e) => update.mutate({ userId: m.user_id, role_in_team: e.target.value as EnrichedMember['role_in_team'] })}
                    disabled={update.isPending}
                  >
                    {(['player', 'lead', 'admin'] as const).map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </td>
                <td className="text-ink-dim">{new Date(m.joined_at).toLocaleDateString()}</td>
                <td className="text-right">
                  <button
                    className="btn-danger"
                    onClick={() => {
                      if (window.confirm(`Remove ${m.display_name} from ${team.name}?`)) remove.mutate(m.user_id);
                    }}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!(members.data?.members ?? []).length ? (
              <tr><td colSpan={6} className="py-4 text-center text-ink-muted">No members yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}